import {
  IconDots,
  IconDownload,
  IconFileDescription,
  IconPencil,
  IconTrash,
} from '@tabler/icons-react';
import clsx from 'clsx';
import { type ReactElement, useRef, useState } from 'react';

import { ActionIcon } from '../ActionIcon';
import {
  EditableText,
  type EditableTextHandle,
  useIsTextOverflowing,
} from '../EditableText';
import { Menu } from '../Menu';
import { ThemeIcon } from '../ThemeIcon';
import { Tooltip } from '../Tooltip';
import styles from './DocumentCard.module.css';
import {
  hasDisguisedExtension,
  isFileNameUsed,
  splitFileName,
} from './fileName.utils';

export interface DocumentCardMenuItem {
  /** Menu item label */
  label: string;
  /** Optional icon rendered to the left of the label */
  icon?: ReactElement;
  /** Click handler */
  onClick: () => void;
}

export interface DocumentCardProps {
  /** File name displayed in the card. Truncates in the middle so the extension stays visible. */
  fileName: string;
  /** Shown on the meta row before the date. Single-line ellipsis with a tooltip when clipped; ignored by `minimal`. */
  description?: string;
  /** Date string to display (e.g. "05/10/2024"). Always shown when provided. */
  date?: string;
  /** Adds the download button. Omit to leave the card without one. */
  onDownload?: () => void;
  /** Items for the three-dot action menu. Pass undefined or empty array to hide the menu button. */
  menuItems?: DocumentCardMenuItem[];
  /** Commits a rename, and adds "Rename" to the top of the menu. The extension is re-appended; empty, unchanged and taken names never commit. */
  onRename?: (newFileName: string) => void;
  /** Other names in the same list, compared with `isFileNameUsed` (canonical: case, underscores, encoding). A collision shows a message and keeps the old name; this card's own name is excluded. Omit to skip the check. */
  existingFileNames?: string[];
  /** Label for the built-in rename menu item. Default: "Rename" */
  renameLabel?: string;
  /** Built in so wording, icon and placement stay identical everywhere. Joins the bottom of the menu, not styled as destructive — or becomes a direct trash button when it is the card's only action. */
  onDelete?: () => void;
  /** Names the delete action: the menu item's text, or the lone button's accessible name. Default: "Delete" */
  deleteLabel?: string;
  /** Default: "border". `border`/`shadow` are the tall two-row card; `minimal` is the compact single row with bordered chrome. */
  variant?: 'border' | 'shadow' | 'minimal';
  className?: string;
}

export const DocumentCard = ({
  fileName,
  description,
  date,
  onDownload,
  menuItems,
  onRename,
  existingFileNames,
  renameLabel = 'Rename',
  onDelete,
  deleteLabel = 'Delete',
  variant = 'border',
  className,
}: DocumentCardProps) => {
  const isMinimal = variant === 'minimal';
  /**
   * A lone delete does not earn a dropdown. Removing the one file a form field
   * holds is a single act, so it reads as a direct trash button; as soon as the
   * card carries any other action, delete rejoins them in the menu.
   */
  const isDeleteInline = Boolean(onDelete) && !onRename && !menuItems?.length;
  const showMenu = Boolean(menuItems?.length) || Boolean(onRename);
  const [mainPart, extPart] = splitFileName(fileName);
  const { ref: descriptionRef, isOverflowing: isDescriptionOverflowing } =
    useIsTextOverflowing(description);

  /** Mirrors the field's state, which the card's own layout has to react to. */
  const [isRenaming, setIsRenaming] = useState(false);
  /** Set by the Rename item, so only that item's close opens the field. */
  const isOpeningRenameRef = useRef(false);
  const fileNameHandleRef = useRef<EditableTextHandle>(null);

  /**
   * Opens the rename field, once the ⋯ menu is gone.
   *
   * While the menu tears down it traps focus and then hands it back to its
   * trigger; a field focused any earlier loses to both and the blur discards
   * the edit. The exit transition's end is past both.
   */
  const handleMenuExitTransitionEnd = () => {
    if (!isOpeningRenameRef.current) {
      return;
    }
    isOpeningRenameRef.current = false;
    fileNameHandleRef.current?.startEditing();
  };

  const actionIconStroke = 1.25;

  const fileNameNode = (
    <EditableText
      ref={fileNameHandleRef}
      className={styles.fileName}
      value={mainPart}
      suffix={extPart}
      onCommit={(nextFileName) => onRename?.(nextFileName)}
      getDraftError={(candidate) => {
        if (isFileNameUsed(candidate, fileName, existingFileNames)) {
          return 'File name already exists';
        }
        if (hasDisguisedExtension(candidate, fileName)) {
          return "The name can't end with a file extension";
        }
        return undefined;
      }}
      editLabel="File name, without the file type"
      onEditingChange={setIsRenaming}
    />
  );

  const downloadNode = onDownload && (
    <ActionIcon
      size="sm"
      className={styles.actionButton}
      onClick={onDownload}
      aria-label="Download file"
    >
      <IconDownload stroke={actionIconStroke} />
    </ActionIcon>
  );

  const deleteNode = isDeleteInline && (
    <ActionIcon
      size="sm"
      className={styles.actionButton}
      onClick={onDelete}
      aria-label={deleteLabel}
    >
      <IconTrash stroke={actionIconStroke} />
    </ActionIcon>
  );

  const menuNode = showMenu && (
    <Menu size="md" onExitTransitionEnd={handleMenuExitTransitionEnd}>
      <Menu.Target>
        <ActionIcon
          size="sm"
          className={styles.actionButton}
          aria-label="More actions"
        >
          <IconDots stroke={actionIconStroke} />
        </ActionIcon>
      </Menu.Target>
      <Menu.Dropdown>
        {onRename && (
          <Menu.Item
            leftSection={<IconPencil size={16} stroke={1.25} />}
            // Opening waits for the menu's exit hook above.
            onClick={() => {
              isOpeningRenameRef.current = true;
            }}
          >
            {renameLabel}
          </Menu.Item>
        )}
        {menuItems?.map((item) => (
          <Menu.Item
            key={item.label}
            leftSection={item.icon}
            onClick={item.onClick}
          >
            {item.label}
          </Menu.Item>
        ))}
        {onDelete && !isDeleteInline && (
          <Menu.Item
            leftSection={<IconTrash size={16} stroke={1.25} />}
            onClick={onDelete}
          >
            {deleteLabel}
          </Menu.Item>
        )}
      </Menu.Dropdown>
    </Menu>
  );

  if (isMinimal) {
    return (
      <div
        className={clsx(styles.root, className)}
        data-variant={variant}
        data-renaming={isRenaming || undefined}
      >
        <div className={styles.minimalMain}>
          <ThemeIcon size="24" color="indigo">
            <IconFileDescription size={16} stroke={1.25} />
          </ThemeIcon>
          {fileNameNode}
        </div>

        <div className={styles.iconGroup}>
          {date && <span className={styles.date}>{date}</span>}
          {downloadNode}
          {deleteNode}
          {menuNode}
        </div>
      </div>
    );
  }

  return (
    <div
      className={clsx(styles.root, className)}
      data-variant={variant}
      data-renaming={isRenaming || undefined}
    >
      <ThemeIcon size="48" color="indigo">
        <IconFileDescription size={24} stroke={1.75} />
      </ThemeIcon>

      <div className={styles.textColumn}>
        <div className={styles.fileRow}>
          {fileNameNode}

          <div className={styles.iconGroup}>
            {downloadNode}
            {deleteNode}
            {menuNode}
          </div>
        </div>

        {(description || date) && (
          <div className={styles.metaRow}>
            {description && (
              <Tooltip
                label={description}
                disabled={!isDescriptionOverflowing}
                position="top"
              >
                <span className={styles.descriptionTarget}>
                  <span ref={descriptionRef} className={styles.description}>
                    {description}
                  </span>
                </span>
              </Tooltip>
            )}
            {description && date && (
              <span className={styles.separatorDot} aria-hidden="true" />
            )}
            {date && <span className={styles.date}>{date}</span>}
          </div>
        )}
      </div>
    </div>
  );
};
